import React, { useEffect } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Modal } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Preloader from "~/components/Preloader";
import { useCreateTariff } from "~/entity/Ivideon/hooks/useCreateTariff";
import { useUpdateTariff } from "~/entity/Ivideon/hooks/useUpdateTariff";
import { IvideonTariffsIProps } from "~/entity/Ivideon/model/schema";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { useNotify } from "~/shared/hooks/useNotify";
import { Field } from "~/shared/ui/Field";

const ivideonPlanFormId = "IvideonPlanForm";

interface IIvideonPlansFormValues {
  name: string;
  price: string;
}

interface IvideonPlanModalIProps {
  reFetch: () => void;
  plans?: IvideonTariffsIProps[];
}

const IvideonPlanModal = ({ reFetch, plans }: IvideonPlanModalIProps) => {
  const { t } = useTranslation();
  const { create } = useCreateTariff();
  const { update } = useUpdateTariff();
  const setParams = useURLParamsSetter();
  const { openModal, currentId } = useURLParamsGetter<{
    openModal: string;
    currentId: string;
  }>();
  const isOpen = ["Create", "Edit"].includes(openModal);

  const { notifySuccess, notifyError } = useNotify();

  const currentPlan =
    plans && plans.find((item) => item.id === Number(currentId));

  const closeModal = () => setParams({ openModal: "", currentId: "" });

  const schema = yup.object().shape({
    name: yup.string().required(t("validation.required")),
    price: yup.string().required(t("validation.required")),
  });

  const { control, handleSubmit, errors, reset } =
    useForm<IIvideonPlansFormValues>({
      resolver: yupResolver(schema),
      defaultValues: {
        name: currentPlan?.name || "",
        price: currentPlan?.price || "",
      },
    });

  // Форма живёт между открытиями модалки — при открытии подставляем
  // значения текущего тарифа (или пустые для создания).
  useEffect(() => {
    if (isOpen) {
      reset({
        name: currentPlan?.name || "",
        price: currentPlan?.price || "",
      });
    }
  }, [openModal, currentId]);

  const onSubmit = (prop: IIvideonPlansFormValues) => {
    if (currentId) {
      const data = {
        id: Number(currentId),
        name: prop.name,
        price: prop.price.toString(),
      };
      update(data, {
        onSuccess: () => {
          notifySuccess(t("ivideonPlans.editedPlan"));
          reFetch();
          closeModal();
        },
        onError: (error) => {
          notifyError(error.message);
        },
      });
    } else {
      const data = { name: prop.name, price: prop.price.toString() };
      create(data, {
        onSuccess: () => {
          notifySuccess(t("ivideonPlans.createdPlan"));
          reFetch();
          closeModal();
        },
        onError: (error) => {
          notifyError(error.message);
        },
      });
    }
  };

  return (
    <Modal
      open={isOpen && openModal !== "ViewIdentifiers"}
      title={t("ivideonPlans.title")}
      onCancel={closeModal}
      closable
      centered
      destroyOnHidden
      cancelButton={{ children: t("ui.cancel") }}
      okButton={{
        htmlType: "submit",
        form: ivideonPlanFormId,
        children: t("ui.save"),
      }}
    >
      {currentId && !plans ? (
        <Preloader />
      ) : (
        <form id={ivideonPlanFormId} onSubmit={handleSubmit(onSubmit)}>
          <Flex vertical gap={10}>
            <Field
              type="input"
              name="name"
              control={control}
              label={t("ivideonPlans.table.name")}
              error={errors["name"]?.message}
            />
            <Field
              type="input"
              name="price"
              control={control}
              label={t("ivideonPlans.table.price")}
              error={errors["price"]?.message}
            />
          </Flex>
        </form>
      )}
    </Modal>
  );
};

export default IvideonPlanModal;
